// §10.6 Deal Terminal Timeline — pure helpers shared by the cockpit
// sub-components. Counters, cycle summaries, and the loss-reason
// rollup all derive from the events array; persisting them on the
// opportunity row is a code smell.

import {
  LOSS_REASONS,
  OpportunityEvent,
  OpportunityEventKind,
} from '../../../types/pipeline';

export interface CycleCounters {
  cycles: number;
  reopens: number;
  won: number;
  lost: number;
}

export function computeCounters(
  events: OpportunityEvent[],
  currentCycle: number,
): CycleCounters {
  return {
    cycles: currentCycle,
    reopens: events.filter((e) => e.kind === 'reopen').length,
    won: events.filter((e) => e.kind === 'won').length,
    lost: events.filter((e) => e.kind === 'lost').length,
  };
}

export interface CycleSummary {
  cycle: number;
  events: OpportunityEvent[];
  closeKind: 'won' | 'lost' | null;
}

export function computeCycleSummaries(
  events: OpportunityEvent[],
): CycleSummary[] {
  const byCycle = new Map<number, CycleSummary>();
  // Walk oldest first so the closeKind reflects the latest terminal
  // event in the cycle.
  const ordered = [...events].sort((a, b) => a.id - b.id);
  for (const e of ordered) {
    let cur = byCycle.get(e.cycle);
    if (!cur) {
      cur = { cycle: e.cycle, events: [], closeKind: null };
      byCycle.set(e.cycle, cur);
    }
    cur.events.push(e);
    if (e.kind === 'won' || e.kind === 'lost') {
      cur.closeKind = e.kind;
    }
  }
  return Array.from(byCycle.values()).sort((a, b) => a.cycle - b.cycle);
}

export type LossRollup = Record<(typeof LOSS_REASONS)[number], number>;

export function computeLossRollup(events: OpportunityEvent[]): LossRollup {
  const out = LOSS_REASONS.reduce<LossRollup>((acc, r) => {
    acc[r] = 0;
    return acc;
  }, {} as LossRollup);
  for (const e of events) {
    if (e.kind === 'lost' && e.loss_kind && e.loss_kind in out) {
      out[e.loss_kind as (typeof LOSS_REASONS)[number]] += 1;
    }
  }
  return out;
}

export type EventFilter = 'all' | OpportunityEventKind;
export type CycleFilter = 'all' | number;

export function filterEvents(
  events: OpportunityEvent[],
  filter: EventFilter,
  cycle: CycleFilter,
  collapsedCycles: Record<number, boolean>,
): OpportunityEvent[] {
  return events.filter((e) => {
    if (filter !== 'all' && e.kind !== filter) return false;
    if (cycle !== 'all' && e.cycle !== Number(cycle)) return false;
    if (collapsedCycles[e.cycle]) return false;
    return true;
  });
}

export function eventToneClass(kind: OpportunityEventKind): string {
  // Maps to DS tokens — bullet markers + dots use these.
  switch (kind) {
    case 'won':
      return 'bg-ok';
    case 'lost':
      return 'bg-danger';
    case 'reopen':
    case 'open':
    default:
      return 'bg-info';
  }
}

export function eventToneText(kind: OpportunityEventKind): string {
  switch (kind) {
    case 'won':
      return 'text-ok-text';
    case 'lost':
      return 'text-danger-text';
    default:
      return 'text-info-text';
  }
}
